/**
 * @file     smsConsent.ts
 * @purpose  The form consent wording and its version, in one place. Every lead
 *           stores the version it agreed to, in Supabase and in Follow Up Boss,
 *           so a second copy that lags behind records the wrong wording.
 */

/**
 * Bump whenever the consent wording changes. The version maps to the exact
 * English text in FORM_CONSENT_COPY below, so an audit reads what the person saw.
 * v5 (2026-10-08): one line under the submit button (FormConsentLine).
 * v6 (2026-10-09): SMS consent is an optional, unticked checkbox
 * (then SmsConsentCheckbox). Only a ticked box records consent.
 * v7 (2026-10-11): the box sits in ConsentChoices beside the email box, under
 * the shared identity line (sender, brokerage, mailing address, contact,
 * withdrawal), so the request is SOR/2012-36 s.4 complete.
 */
export const SMS_CONSENT_VERSION = '2026-10-11-v7';

/**
 * The one box that asks for email and text together (`contactOptIn`). A tick
 * cites this version on both channels, so the ledger records the single label
 * the person saw rather than two boxes that were never shown.
 */
export const CONTACT_CONSENT_VERSION = '2026-10-29-v1';

/** The v6 label, kept verbatim so a v6 grant still records the text it showed. */
export const SMS_OPT_IN_V6_TEXT =
  'Text me about this request from Relta Real Estate. Frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.';

/**
 * The first version recorded from a ticked checkbox. Earlier versions came from
 * submitting under a passive line, which is not express consent, so a stored
 * payload carrying one of them (a pending lead from before the switch) never
 * records consent.
 */
const FIRST_CHECKBOX_CONSENT_VERSION = '2026-10-09-v6';

/**
 * English source of the consent copy. `<terms>` and `<privacy>` mark the
 * links; `{{action}}` is the button's verb (default "continuing").
 * `smsOptIn` is the checkbox label, recorded as the text a ticked box agreed to.
 */
export const FORM_CONSENT_COPY = {
  terms: 'By {{action}}, you agree to the <terms>Terms</terms> and <privacy>Privacy Policy</privacy>.',
  smsOptIn: 'Text me about this request. Frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
  contactOptIn:
    'Email or text me new listings, market updates, and news about Relta tools and services. Frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
} as const;

/** The plain English text of the consent line, as stored for audits. */
export function formConsentText(action = 'continuing'): string {
  return FORM_CONSENT_COPY.terms.replace('{{action}}', action).replace(/<\/?(terms|privacy)>/g, '');
}

/**
 * The payload a form adds for the SMS checkbox. Only a ticked box with a phone
 * sends `smsOptIn`, the field the routes read as consent.
 */
export function smsConsentPayload(
  phone: unknown,
  optedIn: boolean,
): { smsOptIn: true; smsConsentVersion: string } | Record<string, never> {
  return optedIn && typeof phone === 'string' && phone.replace(/\D/g, '').length > 0
    ? { smsOptIn: true, smsConsentVersion: SMS_CONSENT_VERSION }
    : {};
}

/**
 * True only when the request carries the ticked checkbox. The old `smsConsent`
 * field is still accepted on payloads from cached clients, but it was sent for
 * any phone under the passive line, so it never counts on its own.
 */
export function hasExplicitSmsConsent(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as { smsOptIn?: unknown }).smsOptIn === true;
}

/** The SMS version a ticked box cited, for the lead-row audit columns. */
export function submittedSmsConsentVersion(body: unknown): string {
  const cited = typeof body === 'object' && body !== null ? (body as { smsConsentVersion?: unknown }).smsConsentVersion : undefined;
  return cited === CONTACT_CONSENT_VERSION ? CONTACT_CONSENT_VERSION : SMS_CONSENT_VERSION;
}

/** A stored consent version that came from a ticked checkbox. */
export function isCheckboxConsentVersion(version: unknown): boolean {
  return typeof version === 'string' && version >= FIRST_CHECKBOX_CONSENT_VERSION;
}

/** The leads-row audit columns for a consent given at `at`. */
export function smsConsentColumns(consent: {
  at: string;
  version: string;
  ip: string | null;
  userAgent: string | null;
}): Record<string, string | boolean | null> {
  return {
    sms_consent: true,
    sms_consent_timestamp: consent.at,
    sms_consent_version: consent.version,
    sms_consent_ip: consent.ip && consent.ip !== 'unknown' ? consent.ip : null,
    sms_consent_user_agent: consent.userAgent,
  };
}
